/**
 * swRegister — Service Worker registration with version-aware update flow.
 *
 * IMPORTANT: registers ONLY in production builds. The dev server (CRA / yarn
 * start) hot-rebuilds constantly, which combined with the periodic update
 * poll caused a fetch / reload loop on preview. We now skip
 * registration entirely when NODE_ENV !== 'production'. If a SW was registered
 * in a prior session and is still active in the user's browser, the dev-mode
 * branch below unregisters it on load so the page can return to plain
 * network fetches.
 *
 * Update strategy in production:
 *   1. Register the SW on window 'load'.
 *   2. Listen for `updatefound` — when a new SW reaches the `installed`
 *      state AND there's already an active controller, that means a NEW
 *      version is sitting in the wings waiting to activate.
 *   3. Fire a window-level `enferno:sw-update-available` CustomEvent so
 *      UpdateAvailableBanner can show a non-disruptive "New version ready" toast.
 *   4. User taps Refresh → postMessage SKIP_WAITING → controllerchange
 *      fires → page reloads with new bundles.
 *   5. Poll `reg.update()` every UPDATE_POLL_MS so we catch new versions
 *      while the user keeps the app open across a deploy.
 *
 * Emergency rescue: visit any URL with `?sw=off` to unregister all SWs and
 * clear caches. Useful when a stuck SW is breaking a user's session.
 */

const RELOAD_GUARD_KEY = 'enferno_sw_reloaded_once';
const UPDATE_POLL_MS = 60 * 1000;  // poll for new SW versions every minute

let waitingWorkerRef = null;

const fireUpdateAvailable = () => {
  try {
    window.dispatchEvent(new CustomEvent('enferno:sw-update-available', {
      detail: {
        applyUpdate: () => {
          if (waitingWorkerRef) {
            waitingWorkerRef.postMessage('SKIP_WAITING');
          } else {
            window.location.reload();
          }
        },
      },
    }));
  } catch { /* CustomEvent unsupported — ignore */ }
};

// ---------- Emergency rescue: ?sw=off ----------
// Visit any URL with this query string to forcibly unregister every SW and
// clear every cache associated with the origin. Strips the param + reloads.
const handleSwRescue = async () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('sw') !== 'off') return false;
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* ignore */ }
    params.delete('sw');
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.location.replace(newUrl);
  } catch (e) {
    console.warn('[SW] Rescue failed:', e?.message || e);
  }
  return true;
};

// ---------- Dev-mode cleanup ----------
// If a SW was previously registered (e.g. before the dev-gating
// hotfix), unregister it on dev load so the user's browser stops trying to
// run a stale SW that knows nothing about the dev server.
const unregisterAllSWs = () => {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister().catch(() => {})))
    .catch(() => {});
};

export const registerServiceWorker = () => {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  // Always honor the rescue param first, regardless of env.
  handleSwRescue().then((rescued) => {
    if (rescued) return;

    // Dev / non-production: NEVER register a SW. Unregister any leftover.
    if (process.env.NODE_ENV !== 'production') {
      unregisterAllSWs();
      return;
    }

    // ---------- Production registration ----------
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js')
        .then((reg) => {
          if (reg.waiting && navigator.serviceWorker.controller) {
            waitingWorkerRef = reg.waiting;
            fireUpdateAvailable();
          }

          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            if (!newWorker) return;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                waitingWorkerRef = newWorker;
                fireUpdateAvailable();
              }
            });
          });

          setInterval(() => {
            reg.update().catch(() => { /* network blip — ignore */ });
          }, UPDATE_POLL_MS);
        })
        .catch((err) => {
          console.warn('[SW] Registration failed:', err?.message || err);
        });

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        try {
          if (sessionStorage.getItem(RELOAD_GUARD_KEY) === '1') return;
          sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
        } catch { /* ignore */ }
        window.location.reload();
      });
    });
  });
};
